import { GatewayWithSourceViewStateTypes } from "@common/types/gateways-list-types";
import { getLogger } from "@src/cloud-connect/dependency-injection-container/get-logger";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { updateGatewayName } from "@src/cloud-connect/use-cases/update-gateway-name";
import { useI18N } from "@src/language/language-context";

import { RootState } from "@src/reducers/store";
import { updateConnectedGatewaysListData } from "@src/reducers/your-reducer";
import { ChangeEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

export interface UseEditGatewayNameHookProps {
  showInput: boolean;
  editGatewayName: (e) => void;
  onKeyDownFunc: (e) => void;
  updateNewGatewayName: (e, value: string) => void;
  saveGatewayName: (e) => void;
  cancelSaveGatewayName: (e) => void;
  newGatewayName: string;
}

export const updateGatewayNameInGatewaysList = (
  gatewaysList: GatewayWithSourceViewStateTypes[],
  gatewayId: string,
  newGatewayName: string,
): boolean => {
  const gateway = gatewaysList.find((g) => g.gateway_uuid === gatewayId);
  if (gateway && gateway.name !== newGatewayName) {
    gateway.name = newGatewayName;
    return true;
  }
  return false;
};

export const useEditGatewayNameHook = (
  gateway: GatewayWithSourceViewStateTypes,
): UseEditGatewayNameHookProps => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const previousName = gateway.name;

  const connectedGatewaysListData = useSelector(
    (state: RootState) => state.yourSlice.connectedGatewaysListData,
  );

  const logger = getLogger();
  const [showInput, setShowInput] = useState(false);
  const [newGatewayName, setNewGatewayName] = useState(gateway.name);

  const texts = {
    errorInputEmpty: i18n("modules.editGatewayName.errorInputEmpty"),
    errorUpdateFailed: i18n("modules.editGatewayName.errorUpdateFailed"),
    successfullyUpdated: i18n("modules.editGatewayName.successfullyUpdated"),
  };

  const updateNewGatewayName = (e, value: string) => {
    e.stopPropagation();
    logger.debug("Updating name:", value);
    setNewGatewayName(value);
  };

  const editGatewayName = (e) => {
    e.stopPropagation();
    logger.debug("editGatewayName");
    setShowInput(true);
  };

  const onKeyDownFunc = (e) => {
    e.stopPropagation();
    logger.debug("onKeyDownFunc");
    if (e.key === "Enter") {
      e.preventDefault();
      saveGatewayName(e);
    }
  };

  const updateItemNameInReduxState = (newGatewayNameToBeSaved: string) => {
    const clonedGatewaysList = structuredClone(connectedGatewaysListData);

    const didUpdateGatewaysList = updateGatewayNameInGatewaysList(
      clonedGatewaysList,
      gateway.gateway_uuid,
      newGatewayNameToBeSaved,
    );

    if (didUpdateGatewaysList) {
      dispatch(updateConnectedGatewaysListData(clonedGatewaysList));
    }
  };

  const updateGatewayNameInApi = async (newGatewayNameToBeSaved: string) => {
    logger.debug("updateSourceNameInApi", newGatewayNameToBeSaved);

    const response = await updateGatewayName({
      name: newGatewayNameToBeSaved,
      gateway_id: gateway.gateway_uuid,
    });

    return response;
  };

  const saveGatewayName = async (e) => {
    e.stopPropagation();
    logger.debug("saveSourceName", newGatewayName);
    const newGatewayNameToBeSaved = newGatewayName.trim();
    setNewGatewayName(newGatewayNameToBeSaved);

    if (!newGatewayName) {
      errorSnackbar(texts.errorInputEmpty);
      return;
    }

    setShowInput(false);

    const response = await updateGatewayNameInApi(newGatewayNameToBeSaved);

    if (response && response.success) {
      successSnackbar(texts.successfullyUpdated, 5000);
      updateItemNameInReduxState(newGatewayNameToBeSaved);
    } else {
      errorSnackbar(texts.errorUpdateFailed, 5000);
      setNewGatewayName(previousName);
    }
  };

  const cancelSaveGatewayName = (e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    logger.debug("CancelSaveSourceName", newGatewayName);
    setNewGatewayName(previousName);
    setShowInput(false);
  };

  useEffect(() => {
    logger.debug("EditSourceName mounted");
    logger.debug("EditSourceName item", gateway);

    setNewGatewayName(gateway.name);
    return () => {
      logger.debug("EditSourceName unmounted");
    };
  }, []);

  return {
    showInput: showInput,
    editGatewayName: editGatewayName,
    onKeyDownFunc: onKeyDownFunc,
    updateNewGatewayName: updateNewGatewayName,
    saveGatewayName: saveGatewayName,
    cancelSaveGatewayName: cancelSaveGatewayName,
    newGatewayName: newGatewayName,
  };
};
